import {
  deleteV1CompaniesCompanyIdCommandAlkonConnection,
  patchV1CompaniesCompanyIdCommandAlkonConnection,
  postV1CompaniesCompanyIdCommandAlkonConnection,
  putV1CompaniesCompanyIdCommandAlkonConnectionTestConnection,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import {
  SERVICE_UNAVAILABLE,
  UNPROCESSABLE_ENTITY,
} from '~constants/errorMessagesConsts';
import reportRequestError from '~services/reportRequestError';

import { CommandAlkonConnection } from './models';

interface CommandAlkonConnectionCreateInput {
  apiKey: string;
  apiScopeRef: string;
  clientId: string;
  clientSecret: string;
  entityRef: string;
}

function isStatus(error: unknown, status: number) {
  return isAxiosError(error) && error.response?.status === status;
}

function reportFailure(error: unknown, fallbackMessage: string) {
  reportRequestError(
    error,
    isStatus(error, SERVICE_UNAVAILABLE)
      ? t('error_messages.command_alkon.unavailable')
      : fallbackMessage,
  );
}

function handleSaveError(error: unknown) {
  if (isStatus(error, UNPROCESSABLE_ENTITY)) throw error;

  reportFailure(error, t('error_messages.command_alkon.failed_to_save_connection'));

  return null;
}

export default function useCommandAlkon() {
  const createConnection = useCallback(
    async (companyId: string, input: CommandAlkonConnectionCreateInput) => {
      try {
        const response = await postV1CompaniesCompanyIdCommandAlkonConnection({
          body: {
            api_key: input.apiKey,
            api_scope_ref: input.apiScopeRef,
            client_id: input.clientId,
            client_secret: input.clientSecret,
            entity_ref: input.entityRef,
          },
          path: { 'company-id': companyId },
        });

        return CommandAlkonConnection.parse(response.data.data);
      } catch (error) {
        return handleSaveError(error);
      }
    },
    [],
  );

  const updateConnection = useCallback(
    async (companyId: string, input: Partial<CommandAlkonConnectionCreateInput>) => {
      try {
        const response = await patchV1CompaniesCompanyIdCommandAlkonConnection({
          body: {
            api_key: input.apiKey,
            api_scope_ref: input.apiScopeRef,
            client_id: input.clientId,
            client_secret: input.clientSecret,
            entity_ref: input.entityRef,
          },
          path: { 'company-id': companyId },
        });

        return CommandAlkonConnection.parse(response.data.data);
      } catch (error) {
        return handleSaveError(error);
      }
    },
    [],
  );

  const deleteConnection = useCallback(async (companyId: string) => {
    try {
      await deleteV1CompaniesCompanyIdCommandAlkonConnection({
        path: { 'company-id': companyId },
      });

      return true;
    } catch (error) {
      reportRequestError(
        error,
        t('error_messages.command_alkon.failed_to_delete_connection'),
      );

      return false;
    }
  }, []);

  const testConnection = useCallback(async (companyId: string) => {
    try {
      const response = await putV1CompaniesCompanyIdCommandAlkonConnectionTestConnection({
        path: { 'company-id': companyId },
      });

      return response.data.data.success;
    } catch (error) {
      reportFailure(error, t('error_messages.command_alkon.failed_to_test_connection'));

      return null;
    }
  }, []);

  return { createConnection, deleteConnection, testConnection, updateConnection };
}
